import { useWalletDetails } from "@sundae/wallet-react";
import {
  EDatumType,
  IMigrateYieldFarmingLiquidityConfig,
  IPoolData,
  IWithdrawConfigArgs,
} from "@sundaeswap/core";
import isEqual from "lodash/isEqual";
import { useMemo } from "react";
import { useCustomCompareMemo } from "use-custom-compare";
import { useMigrateLiquidityContext } from "../../components/Dialogs/MigrateLiquidityDialog/context";
import { useYieldFarmingPositions } from "../farms/useYieldFarmingPositions";
import { useAssetsWithBalance } from "../useAssetsWithBalance";

export const useMigrateLiquidityConfig = () => {
  const {
    derived: { selectedPools },
  } = useMigrateLiquidityContext();
  const {
    details: { mainAddress, stakingAddress },
  } = useWalletDetails();
  const { lpAssets } = useAssetsWithBalance();
  const { lpAssetsPoolMap, mergedPosition } = useYieldFarmingPositions({
    beneficiary: [mainAddress, stakingAddress].sort(),
  });

  const migrateLiquidityConfig = useCustomCompareMemo(
    () =>
      selectedPools
        .map(({ V1: v1Pool, V3: v3Pool }) => {
          const matchingLpAssetBalance = lpAssets?.find(
            (asset) => asset.metadata.assetId === v1Pool.assetLP.assetId
          );

          if (!matchingLpAssetBalance) {
            return null;
          }

          return {
            withdrawConfig: {
              pool: {
                assetA: v1Pool.assetA,
                assetB: v1Pool.assetB,
                assetLP: v1Pool.assetLP,
                currentFee: Number(v1Pool.fee) / 100,
                ident: v1Pool.ident,
                liquidity: {
                  aReserve: BigInt(v1Pool.quantityA || 0),
                  bReserve: BigInt(v1Pool.quantityB || 0),
                  lpTotal: BigInt(v1Pool.quantityLP || 0),
                },
                version: v1Pool.version,
              },
              suppliedLPAsset: matchingLpAssetBalance?.quantity,
              orderAddresses: {
                DestinationAddress: {
                  address: mainAddress,
                  datum: {
                    type: EDatumType.NONE,
                  },
                },
              },
            } as IWithdrawConfigArgs,
            depositPool: {
              assetA: v3Pool.assetA,
              assetB: v3Pool.assetB,
              assetLP: v3Pool.assetLP,
              currentFee: Number(v3Pool.fee),
              liquidity: {
                aReserve: BigInt(v3Pool.quantityA || 0),
                bReserve: BigInt(v3Pool.quantityB || 0),
                lpTotal: BigInt(v3Pool.quantityLP || 0),
              },
              version: v3Pool.version,
              ident: v3Pool.ident,
            } as IPoolData,
          };
        })
        // filter nulls
        .filter(Boolean) as {
        withdrawConfig: IWithdrawConfigArgs;
        depositPool: IPoolData;
      }[],
    [selectedPools, lpAssets, mainAddress],
    isEqual
  );

  const yfMigrationConfigs: IMigrateYieldFarmingLiquidityConfig | undefined = useMemo(() => {
    if (
      lpAssetsPoolMap.size === 0 ||
      !mergedPosition ||
      mergedPosition.delegation.length === 0 ||
      mergedPosition.value.length === 0 ||
      mergedPosition?.inputs?.length === 0
    ) {
      return undefined;
    }

    const configs: IMigrateYieldFarmingLiquidityConfig = {
      existingPositions:
        mergedPosition?.inputs?.map(({ txHash, index }) => ({
          hash: txHash,
          index,
        })) ?? [],
      ownerAddress: {
        address: mainAddress,
        datum: {
          type: EDatumType.NONE,
        },
      },
      migrations: [],
    };

    selectedPools.forEach(({ V1, V3 }) => {
      for (const lockedTokensPool of mergedPosition.value) {
        if (V1.ident === lockedTokensPool.pool?.ident) {
          configs.migrations.push({
            withdrawPool: {
              assetA: V1.assetA,
              assetB: V1.assetB,
              assetLP: V1.assetLP,
              currentFee: Number(V1.fee) / 100,
              ident: V1.ident,
              liquidity: {
                aReserve: BigInt(V1.quantityA || 0),
                bReserve: BigInt(V1.quantityB || 0),
                lpTotal: BigInt(V1.quantityLP || 0),
              },
              version: V1.version,
            },
            depositPool: {
              assetA: V3.assetA,
              assetB: V3.assetB,
              assetLP: V3.assetLP,
              currentFee: Number(V3.fee),
              liquidity: {
                aReserve: BigInt(V3.quantityA || 0),
                bReserve: BigInt(V3.quantityB || 0),
                lpTotal: BigInt(V3.quantityLP || 0),
              },
              version: V3.version,
              ident: V3.ident,
            },
          });
        }
      }
    });

    return configs;
  }, [lpAssetsPoolMap, stakingAddress, mergedPosition?.inputs]);

  return {
    migrateLiquidityConfig,
    yfMigrationConfigs,
  };
};
